import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import SignInPromptModal from "../components/SignInPromptModal";

const UserCtx = createContext({ user: null, loading: true, signIn: () => {}, signOut: () => {} });

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState({ open: false, returnTo: null, reason: "" });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me-user");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // The session handler will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const doRedirect = (returnTo) => {
    const target = returnTo || window.location.pathname + window.location.search;
    const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(target);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  /** Open the confirmation prompt first. Pass `{ skipPrompt: true }` to redirect immediately. */
  const signIn = (arg1, arg2) => {
    // Back-compat: signIn(returnTo) or signIn(returnTo, "reason text") or signIn({ returnTo, reason, skipPrompt })
    let returnTo = null;
    let reason = "";
    let skipPrompt = false;
    if (typeof arg1 === "object" && arg1 !== null) {
      returnTo = arg1.returnTo || null;
      reason = arg1.reason || "";
      skipPrompt = Boolean(arg1.skipPrompt);
    } else {
      returnTo = arg1 || null;
      reason = typeof arg2 === "string" ? arg2 : "";
    }
    if (skipPrompt) {
      doRedirect(returnTo);
      return;
    }
    setPrompt({ open: true, returnTo, reason });
  };

  const signOut = async () => {
    try { await api.post("/auth/logout-user"); } catch {}
    setUser(null);
  };

  return (
    <UserCtx.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
      <SignInPromptModal
        open={prompt.open}
        reason={prompt.reason}
        onClose={() => setPrompt((p) => ({ ...p, open: false }))}
        onConfirm={() => {
          const rt = prompt.returnTo;
          setPrompt((p) => ({ ...p, open: false }));
          doRedirect(rt);
        }}
      />
    </UserCtx.Provider>
  );
}

export const useUser = () => useContext(UserCtx);
